import { useEffect, useMemo, useRef } from 'react';
import { useTranslate } from '../../application/i18n/LocaleProvider';
import { useCarCatalogue } from '../../application/cars/useCarCatalogue';
import { useBrowseFilters } from '../../application/cars/browseState';
import {
    NO_FILTERS,
    countActiveFilters,
    filterCars,
    isFacetActive,
    listFacets,
    toggleFacetValue,
} from '../../domain/cars/carFilters';
import { CloseIcon } from '../icons/ControlIcons';
import FacetPill from './FacetPill';

/**
 * The whole filter model, reachable from the header (SPEC-003).
 *
 * It exists because the header is STICKY and the pill row above the grid is not:
 * a reader eight cards down who wants to narrow to Japanese icons would
 * otherwise have to scroll back to the top to reach a control. The row is how
 * you take one filter off while you can see it; this is how you reach the model
 * from anywhere.
 *
 * The two are not two states of one feature that have to be kept in step. They
 * are two drawings of the SAME state, and the state is the URL — so a pill
 * pressed in here is already pressed out there before this closes, with no
 * wiring at all between them. That is the whole return on putting the selection
 * in the query rather than in a provider.
 *
 * A real `<dialog>` opened with `showModal()`: focus trapping, Escape, the top
 * layer and an inert background are four things the platform gets right and a
 * hand-rolled bottom sheet gets subtly wrong.
 *
 * It reads the catalogue itself rather than being handed it. The hook is a
 * shared read of a document that is already fetched by the time a reader can
 * press the button that mounts this, so asking again costs nothing — and being
 * self-contained is what lets the header own it without the header learning
 * anything about cars.
 */
export default function GarageFilterSheet({ onClose }) {
    const t = useTranslate();
    const { cars } = useCarCatalogue();
    const { filters, setFilters } = useBrowseFilters();
    const dialogRef = useRef(null);

    const activeCount = countActiveFilters(filters);
    const facets = useMemo(() => listFacets(cars), [cars]);
    // The number on the confirming button, so the reader can see a filter empty
    // the garage BEFORE they close the sheet on an empty screen.
    const resultCount = useMemo(() => filterCars(cars, filters).length, [cars, filters]);

    // Opening is a one-off, and deliberately its own effect: an effect that
    // depended on `onClose` would close and re-open the dialog whenever the
    // parent re-rendered, and the second `showModal()` throws.
    useEffect(() => {
        const dialog = dialogRef.current;

        dialog.showModal();

        return () => {
            if (dialog.open) {
                dialog.close();
            }
        };
    }, []);

    // One exit for every way out: the button closes it, and so do Escape and a
    // tap on the backdrop, which the browser turns into this same event.
    useEffect(() => {
        const dialog = dialogRef.current;

        dialog.addEventListener('close', onClose);

        return () => dialog.removeEventListener('close', onClose);
    }, [onClose]);

    const applyLabel = () => {
        if (resultCount === 0) {
            return t('garage.filter.applyNone');
        }

        return resultCount === 1 ? t('garage.filter.applyOne') : t('garage.filter.apply', { count: resultCount });
    };

    return (
        <dialog
            className="filter-sheet"
            ref={dialogRef}
            aria-labelledby="filter-sheet-title"
            // A click that lands on the dialog element ITSELF landed on the
            // backdrop: everything inside is a child of the panel.
            onClick={(event) => {
                if (event.target === dialogRef.current) {
                    dialogRef.current.close();
                }
            }}
        >
            <header className="filter-sheet__head">
                <h2 className="filter-sheet__title" id="filter-sheet-title">{t('garage.filter.title')}</h2>
                <button
                    type="button"
                    className="round-button"
                    onClick={() => dialogRef.current.close()}
                    aria-label={t('garage.filter.close')}
                >
                    <CloseIcon size={20} />
                </button>
            </header>

            <div className="filter-sheet__groups">
                {facets.map((facet) => (
                    <section className="filter-sheet__group" key={facet.key} aria-labelledby={`sheet-facet-${facet.key}`}>
                        <h3 className="filter-sheet__legend" id={`sheet-facet-${facet.key}`}>{t(facet.labelKey)}</h3>
                        <div className="filter-sheet__options">
                            {facet.options.map((option) => (
                                <FacetPill
                                    key={option.value}
                                    label={t(option.labelKey)}
                                    isActive={isFacetActive(filters, facet.key, option.value)}
                                    onToggle={() => setFilters(toggleFacetValue(filters, facet.key, option.value))}
                                />
                            ))}
                        </div>
                    </section>
                ))}
            </div>

            <footer className="filter-sheet__foot">
                {activeCount > 0 ? (
                    <button type="button" className="button button--quiet" onClick={() => setFilters(NO_FILTERS)}>
                        {t('garage.filter.clear')}
                    </button>
                ) : null}
                {/* Confirms rather than applies: the filters are already live
                    behind the sheet, so this closes it. Naming it for the result
                    is what makes that honest — "Show 4 legends" is a promise the
                    garage has already kept. */}
                <button type="button" className="button button--primary" onClick={() => dialogRef.current.close()}>
                    {applyLabel()}
                </button>
            </footer>
        </dialog>
    );
}
